// The design's "History" panel ("Jul 14 · AI categorised Lodging · Undo")
// needs a labelling-audit-log endpoint that does not exist in M1. Rather
// than fabricate timestamps or an Undo action with no backend, this renders
// one real line per allocation from data the API DOES return (amount,
// category) and omits the date and Undo, which would otherwise be invented.
// It never shows a confidence dot or level (design §9.3, D5: "do not show
// the low confidence label"), and -- merging e2e_wire's canonical/honest
// presentation with PR #22 -- it never shows the categorization source
// either: `publicAllocationDetails` projects only display-safe fields, so
// `source`/`confidence` cannot reach this component at all (AGENTS.md Data
// & UI honesty 4).
//
// From PR #22 (ENG-R1-004/DES-R1-001, ENG-R3-001): the category is resolved
// to its display NAME, never the raw slug. Resolution is by slug against the
// taxonomy the caller already loaded -- the projection deliberately carries
// no `category_id`.
import type { PrimaryTransactionDetail } from '@/api/hooks/useTransaction';
import type { Category } from '@/api/hooks/useCategories';
import { AmountText } from '@/components/AmountText';

export type PublicAllocationDetail = {
  amount: string;
  category_slug: string | null;
};

type AllocationInput = Pick<PrimaryTransactionDetail['line_items'][number], 'amount' | 'category_slug'>;

export function publicAllocationDetails(lineItems: readonly AllocationInput[]): PublicAllocationDetail[] {
  return lineItems.map(({ amount, category_slug }) => ({
    amount,
    category_slug,
  }));
}

// Unresolved categories (still loading OR the fetch failed) render a neutral
// placeholder -- never the slug and never a false "Uncategorized" for an
// allocation that has a category (ENG-R2-001). A loaded list with no match is
// an unknown category, stated as such (ENG-R3-001).
function categoryLabel(
  allocation: PublicAllocationDetail,
  categories: Category[] | undefined,
  categoriesLoading: boolean,
): string {
  if (!allocation.category_slug) return 'Uncategorized';
  const match = categories?.find((c) => c.slug === allocation.category_slug);
  if (match) return match.name;
  if (categories === undefined || categoriesLoading) return '…';
  return 'Unknown category';
}

export function HistoryList({
  allocations,
  currency,
  categories,
  categoriesLoading,
}: {
  allocations: readonly PublicAllocationDetail[];
  currency: string;
  /** The full taxonomy (e.g. `useCategories().data?.categories`), used only to resolve each allocation's category NAME. */
  categories?: Category[];
  categoriesLoading?: boolean;
}) {
  if (allocations.length === 0) return null;
  return (
    <div data-testid="history-list" className="mt-3 text-xs">
      <div className="font-semibold text-foreground">Allocations</div>
      <ul className="mt-1 space-y-1.5 text-muted-foreground">
        {allocations.map((item, index) => (
          <li key={`${item.category_slug ?? 'uncategorized'}-${index}`} className="flex items-center justify-between gap-1.5">
            <span>{categoryLabel(item, categories, categoriesLoading ?? false)}</span>
            <AmountText value={item.amount} currency={currency} className="text-xs" />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default HistoryList;
